import { AlertTriangle, ExternalLink, FileText, FileWarning, ImageIcon } from "lucide-react"
import { observer } from "mobx-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { openPathInNativeApp } from "../electronAPIs/shell"
import type { NestFileBytes } from "../nest/fileBytes"
import type { FileBrowserManager } from "../store/managers/FileBrowserManager"
import { type AnnotationSide, type CommentHandlers, FileViewer } from "./FilesAndDiffs"
import { FilePathBar } from "./FilePathBar"
import { scrollFileViewerToLine } from "./utils/fileViewerScroll"
import { useNativeHostPathActionsAvailable } from "./utils/nativeHostPathActions"

// Above this, downloading and decoding is a deliberate choice rather than a
// side effect of clicking a link.
const IMAGE_AUTO_PREVIEW_BYTES = 25 * 1024 * 1024
const PDF_AUTO_PREVIEW_BYTES = 50 * 1024 * 1024

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface FileContentViewerProps {
    fileBrowser: FileBrowserManager
    taskId: string
    className?: string
    showPathBar?: boolean
}

function OpenNativeButton({ path, label = "Open" }: { path: string; label?: string }) {
    const available = useNativeHostPathActionsAvailable()
    if (!available) return null
    return (
        <button
            type="button"
            className="btn inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-base-200 text-base-content hover:bg-base-300 border border-border text-xs font-medium transition-colors cursor-pointer"
            onClick={() => openPathInNativeApp(path)}
            title="Open in default app"
        >
            <ExternalLink size={14} />
            <span>{label}</span>
        </button>
    )
}

function hostOpenDetail(desktopDetail: string, available: boolean): string {
    return available ? desktopDetail : "Open this file from WildLark on the computer to use its native app."
}

export function FileMessage({ icon, title, detail, action }: { icon?: React.ReactNode; title: string; detail?: string; action?: React.ReactNode }) {
    return (
        <div className="flex flex-col items-center justify-center py-12 px-4 gap-3 text-center">
            {icon ?? null}
            <div>
                <div className="text-sm text-base-content">{title}</div>
                {detail && <div className="mt-1 text-xs text-muted">{detail}</div>}
            </div>
            {action}
        </div>
    )
}

export type FileBytesReader = (options: { signal?: AbortSignal }) => Promise<NestFileBytes>

function useFileObjectURL({ loadKey, readFileBytes, shouldLoad }: { loadKey: string; readFileBytes: FileBytesReader; shouldLoad: boolean }) {
    const [error, setError] = useState<string | null>(null)
    const [src, setSrc] = useState<string | null>(null)

    useEffect(() => {
        if (!shouldLoad) return
        const controller = new AbortController()
        let objectURL: string | null = null
        setError(null)
        setSrc(null)
        void readFileBytes({ signal: controller.signal })
            .then((bytes) => {
                if (controller.signal.aborted) return
                objectURL = URL.createObjectURL(bytes.blob)
                setSrc(objectURL)
            })
            .catch((cause: unknown) => {
                if (controller.signal.aborted) return
                setError(cause instanceof Error ? cause.message : "File preview failed")
            })
        return () => {
            controller.abort()
            if (objectURL) URL.revokeObjectURL(objectURL)
        }
    }, [loadKey, readFileBytes, shouldLoad])

    return { error, src, failPreview: setError }
}

function ImagePreview({
    path,
    mediaType,
    size,
    readFileBytes,
    allowNativeOpen,
}: {
    path: string
    mediaType?: string | null
    size: number
    readFileBytes: FileBytesReader
    allowNativeOpen: boolean
}) {
    const [confirmedLarge, setConfirmedLarge] = useState(false)
    const shouldLoad = size <= IMAGE_AUTO_PREVIEW_BYTES || confirmedLarge
    const { error, src, failPreview } = useFileObjectURL({ loadKey: path, readFileBytes, shouldLoad })

    if (error) {
        return (
            <FileMessage
                icon={<AlertTriangle size={24} className="text-warning" />}
                title="Image preview failed"
                detail={error}
                action={allowNativeOpen ? <OpenNativeButton path={path} /> : undefined}
            />
        )
    }

    if (!shouldLoad) {
        return (
            <FileMessage
                icon={<ImageIcon size={24} className="text-muted" />}
                title={`Large image (${formatFileSize(size)})`}
                detail="Previewing it will download and decode the whole file."
                action={
                    <button
                        type="button"
                        className="btn inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-base-200 text-base-content hover:bg-base-300 border border-border text-xs font-medium transition-colors cursor-pointer"
                        onClick={() => setConfirmedLarge(true)}
                    >
                        Preview anyway
                    </button>
                }
            />
        )
    }

    return (
        <div className="h-full min-h-full bg-editor-background flex flex-col">
            <div className="shrink-0 flex items-center justify-between gap-3 px-3 py-2 border-b border-border bg-base-100">
                <div className="min-w-0 flex items-center gap-2 text-xs text-muted">
                    <ImageIcon size={14} className="shrink-0" />
                    <span className="truncate">{mediaType ?? "Image"}</span>
                    <span className="shrink-0">· {formatFileSize(size)}</span>
                </div>
                {allowNativeOpen ? <OpenNativeButton path={path} /> : null}
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-4">
                <div className="min-h-full flex items-center justify-center">
                    {src ? (
                        <img
                            src={src}
                            alt=""
                            decoding="async"
                            draggable={false}
                            onError={() => failPreview("The image could not be decoded.")}
                            className="block max-w-full max-h-[calc(100vh-12rem)] object-contain"
                        />
                    ) : (
                        <ImageIcon size={24} className="text-muted animate-pulse" />
                    )}
                </div>
            </div>
        </div>
    )
}

function PdfPreview({
    path,
    mediaType,
    size,
    readFileBytes,
    allowNativeOpen,
}: {
    path: string
    mediaType?: string | null
    size: number
    readFileBytes: FileBytesReader
    allowNativeOpen: boolean
}) {
    const [confirmedLarge, setConfirmedLarge] = useState(false)
    const shouldLoad = size <= PDF_AUTO_PREVIEW_BYTES || confirmedLarge
    const { error, src } = useFileObjectURL({ loadKey: path, readFileBytes, shouldLoad })

    if (error) {
        return (
            <FileMessage
                icon={<AlertTriangle size={24} className="text-warning" />}
                title="PDF preview failed"
                detail={error}
                action={allowNativeOpen ? <OpenNativeButton path={path} /> : undefined}
            />
        )
    }

    if (!shouldLoad) {
        return (
            <FileMessage
                icon={<FileText size={24} className="text-muted" />}
                title={`Large PDF (${formatFileSize(size)})`}
                detail="Previewing it will download the whole document."
                action={
                    <button
                        type="button"
                        className="btn inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-base-200 text-base-content hover:bg-base-300 border border-border text-xs font-medium transition-colors cursor-pointer"
                        onClick={() => setConfirmedLarge(true)}
                    >
                        Preview anyway
                    </button>
                }
            />
        )
    }

    return (
        <div className="flex h-full min-h-[32rem] flex-col bg-editor-background">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border bg-base-100 px-3 py-2">
                <div className="flex min-w-0 items-center gap-2 text-xs text-muted">
                    <FileText size={14} className="shrink-0" />
                    <span className="truncate">{mediaType ?? "application/pdf"}</span>
                    <span className="shrink-0">· {formatFileSize(size)}</span>
                </div>
                {allowNativeOpen ? <OpenNativeButton path={path} /> : null}
            </div>
            <div className="min-h-0 flex-1">
                {src ? (
                    <iframe src={src} title="PDF preview" className="block h-full min-h-[32rem] w-full border-0 bg-white" />
                ) : (
                    <div className="flex h-full min-h-[32rem] items-center justify-center">
                        <FileText size={24} className="animate-pulse text-muted" />
                    </div>
                )}
            </div>
        </div>
    )
}

export function FileBytesPreview({
    path,
    mediaType,
    size,
    previewKind,
    readFileBytes,
    allowNativeOpen = false,
}: {
    path: string
    mediaType?: string | null
    size: number
    previewKind: "image" | "pdf"
    readFileBytes: FileBytesReader
    allowNativeOpen?: boolean
}) {
    if (previewKind === "image") {
        return <ImagePreview path={path} mediaType={mediaType} size={size} readFileBytes={readFileBytes} allowNativeOpen={allowNativeOpen} />
    }
    return <PdfPreview path={path} mediaType={mediaType} size={size} readFileBytes={readFileBytes} allowNativeOpen={allowNativeOpen} />
}

export const FileContentViewer = observer(function FileContentViewer({ fileBrowser, taskId, className, showPathBar = true }: FileContentViewerProps) {
    const { activeFile, activeFileData, activeLine, fileLoading, fileError } = fileBrowser
    const contentRef = useRef<HTMLDivElement>(null)
    const nativeHostPathActionsAvailable = useNativeHostPathActionsAvailable()
    const previewPath = activeFileData?.path ?? ""
    const readPreviewBytes = useMemo<FileBytesReader>(() => (options) => fileBrowser.readFileBytes(previewPath, options), [fileBrowser, previewPath])

    // Create comment handlers for the current file
    const commentHandlers: CommentHandlers | null = useMemo(() => {
        if (!activeFile) return null

        const sourceMatch = (c: { source: { type: string; filePath?: string } }) => c.source.type === "file" && c.source.filePath === activeFile

        const createSource = (lineStart: number, lineEnd: number, _side: AnnotationSide) => ({
            type: "file" as const,
            filePath: activeFile,
            lineStart,
            lineEnd,
        })

        return { taskId, sourceMatch, createSource }
    }, [taskId, activeFile])

    useEffect(() => {
        if (!activeLine || fileLoading || !activeFileData?.content) return

        const timeoutId = setTimeout(() => {
            scrollFileViewerToLine(contentRef.current, activeLine)
        }, 50)

        return () => clearTimeout(timeoutId)
    }, [activeFile, activeFileData?.content, activeLine, fileLoading])

    if (!activeFile) {
        return <div className={twMerge("flex flex-col h-full items-center justify-center text-muted text-sm", className)}>Select a file to view</div>
    }

    return (
        <div className={twMerge("flex flex-col h-full", className)}>
            {showPathBar ? <FilePathBar path={activeFile} workingDir={fileBrowser.workingDir} line={activeLine} /> : null}
            <div ref={contentRef} className="min-h-0 flex-1 overflow-auto">
                {fileLoading ? (
                    <div className="flex items-center justify-center py-12 text-muted text-sm">Loading file...</div>
                ) : fileError ? (
                    <FileMessage icon={<AlertTriangle size={24} className="text-error" />} title={fileError} />
                ) : activeFileData && activeFileData.isReadable === false ? (
                    <FileMessage icon={<AlertTriangle size={24} className="text-warning" />} title="File is not readable" />
                ) : activeFileData?.previewKind === "image" ? (
                    <FileBytesPreview
                        key={activeFileData.path}
                        path={activeFileData.path}
                        mediaType={activeFileData.mediaType}
                        size={activeFileData.size}
                        previewKind="image"
                        readFileBytes={readPreviewBytes}
                        allowNativeOpen
                    />
                ) : activeFileData?.previewKind === "pdf" ? (
                    <FileBytesPreview
                        key={activeFileData.path}
                        path={activeFileData.path}
                        mediaType={activeFileData.mediaType}
                        size={activeFileData.size}
                        previewKind="pdf"
                        readFileBytes={readPreviewBytes}
                        allowNativeOpen
                    />
                ) : activeFileData?.tooLarge ? (
                    <FileMessage
                        icon={<AlertTriangle size={24} className="text-warning" />}
                        title={`File too large to display (${formatFileSize(activeFileData.size)})`}
                        detail={hostOpenDetail("Open it in the default app instead.", nativeHostPathActionsAvailable)}
                        action={<OpenNativeButton path={activeFileData.path} />}
                    />
                ) : activeFileData?.isBinary ? (
                    <FileMessage
                        icon={<FileWarning size={24} className="text-warning" />}
                        title="Binary file cannot be displayed as text"
                        detail={
                            nativeHostPathActionsAvailable
                                ? activeFileData.mediaType
                                    ? `${activeFileData.mediaType} · ${formatFileSize(activeFileData.size)}`
                                    : formatFileSize(activeFileData.size)
                                : hostOpenDetail("", nativeHostPathActionsAvailable)
                        }
                        action={<OpenNativeButton path={activeFileData.path} />}
                    />
                ) : activeFileData && activeFileData.content !== null ? (
                    <div className="min-h-full bg-editor-background">
                        <FileViewer
                            file={{
                                name: activeFile,
                                contents: activeFileData.content,
                            }}
                            disableFileHeader
                            commentHandlers={commentHandlers}
                            highlightLines={activeLine ? { start: activeLine, end: activeLine } : null}
                        />
                    </div>
                ) : (
                    <div className="flex items-center justify-center py-12 text-muted text-sm">Unable to load file content</div>
                )}
            </div>
        </div>
    )
})
